/**
 * @module actions/AudioPlayer
 */
import { createAction } from 'typesafe-actions';

import { AudioPlayerActions } from '../constants/AudioPlayer';
import { getNow } from '../lib/TimeUtils';

interface AudioPlayTrackParams {
  playlistId: $TSFixMe;
  track: $TSFixMe;
  currentTime: number;
  isInitialLoad: $TSFixMe;
}

interface AudioPlayTrackPayload extends Exclude<AudioPlayTrackParams, 'currentTime'> {
  resume: boolean;
  AUDIO_CURRENT_TIME: number;
  CURRENT_TIMESTAMP: number;
}

export const playTrack = createAction(
  AudioPlayerActions.AUDIO_PLAY_TRACK,

  ({ playlistId, track, currentTime = 0, isInitialLoad }: AudioPlayTrackParams) => ({
    playlistId,
    track,
    resume: currentTime > 0,
    AUDIO_CURRENT_TIME: currentTime,
    CURRENT_TIMESTAMP: getNow(),
    isInitialLoad,
  }),
)<AudioPlayTrackPayload>();
export type AudioPlayTrackAction = ReturnType<typeof playTrack>;

interface AudioPreloadTrackParams {
  track: $TSFixMe;
}

export const preloadTrack = createAction(
  AudioPlayerActions.AUDIO_PRELOAD_TRACK,
  ({ track }: AudioPreloadTrackParams) => ({ track }),
)<AudioPreloadTrackParams>();
export type AudioPreloadTrackAction = ReturnType<typeof preloadTrack>;

interface PauseParams {
  track: $TSFixMe;
  isExplicit: boolean;
}

export const pauseAudio = createAction(
  AudioPlayerActions.PAUSE,
  ({ track, isExplicit = false }: PauseParams) => ({ track, isExplicit }),
)<PauseParams>();
export type PauseAction = ReturnType<typeof pauseAudio>;

type SetActiveTrackUrlParams = $TSFixMe;

interface SetActiveTrackUrlPayload {
  url: SetActiveTrackUrlParams;
}

export const setActiveTrackUrl = createAction(
  AudioPlayerActions.SET_ACTIVE_TRACK_URL,
  (url: SetActiveTrackUrlParams) => ({ url }),
)<SetActiveTrackUrlPayload>();
export type SetActiveTrackUrlAction = ReturnType<typeof setActiveTrackUrl>;

interface ScrubAudioPayload {
  value: number;
}

export const scrubAudio = createAction(AudioPlayerActions.SCRUB_AUDIO, (value = 0) => ({
  value,
}))<ScrubAudioPayload>();
export type ScrubAudioAction = ReturnType<typeof scrubAudio>;

interface SetVolumePayload {
  volume: number;
}

/**
 * Special action for initiating _volume change_ of the audio player. Will
 * ultimately be mapped to the global volume state.
 * @param {Number} volume The volume value from 0-100 inclusive on a 100 point scale
 */
export const setVolumeAudio = createAction(AudioPlayerActions.SET_VOLUME, (volume: number) => ({
  volume,
}))<SetVolumePayload>();
export type SetVolumeAction = ReturnType<typeof setVolumeAudio>;

/**
 * Special action for initiating _unmute_ of the audio player. Will ultimately
 * be mapped to the global muted state.
 */
export const muteAudio = createAction(AudioPlayerActions.MUTE)();
export type MuteAction = ReturnType<typeof muteAudio>;

/**
 * Special action for initiating _mute_ of the audio player. Will ultimately
 * be mapped to the global muted state.
 */
export const unmuteAudio = createAction(AudioPlayerActions.UNMUTE)();
export type UnmuteAction = ReturnType<typeof unmuteAudio>;

/**
 * Reset audio player
 */
export const resetAudio = createAction(AudioPlayerActions.RESET)();
export type ResetAction = ReturnType<typeof resetAudio>;

interface HandlePostUrlUpdatePayload {
  newUrl: $TSFixMe;
  track: $TSFixMe;
}

export const handlePostUrlUpdate = createAction(
  AudioPlayerActions.HANDLE_POST_URL_UPDATE,
  (newUrl: $TSFixMe, track: $TSFixMe) => ({ newUrl, track }),
)<HandlePostUrlUpdatePayload>();
export type HandlePostUrlUpdateAction = ReturnType<typeof handlePostUrlUpdate>;
